import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './staffListDialog.module.scss'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { Close, Groups, Person } from '@mui/icons-material'
import { Staff } from '@/types/Staff'
import { GET_STAFF_LIST } from '@/modules/API/staff/staffHandler'
import { useLazyQuery } from '@apollo/client'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'

interface StaffListDialogProps {
    isDialogOpen: boolean
    handleCloseDialog: () => void
    showStaffDaycareId: number
}

const StaffListDialog: FunctionComponent<StaffListDialogProps> = ({
    isDialogOpen,
    handleCloseDialog,
    showStaffDaycareId
}: StaffListDialogProps) => {
    const { t } = useTranslation()

    const { setCurrentDaycare } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [staffs, setStaffs] = useState<Staff[]>([])

    const [getStaffs, { data: dataStaffList }] = useLazyQuery(GET_STAFF_LIST, {
        variables: {
            daycareId: showStaffDaycareId
        }
    })

    useEffect(() => {
        if (dataStaffList) {
            setStaffs(dataStaffList.getStaffList)
        }
    }, [dataStaffList])

    useEffect(() => {
        setStaffs([])
        if (showStaffDaycareId) {
            getStaffs()
        }
    }, [showStaffDaycareId])

    return (
        <Dialog open={isDialogOpen} handleClose={handleCloseDialog} className={styles.Dialog}>
            <DialogHeader title={t('staffList')} />
            <DialogBody className={styles.DialogBody}>
                <TableContainer className={styles.TablePlacement}>
                    <Table className={styles.Table}>
                        <TableHead className={styles.TableHeader}>
                            <TableRow>
                                <TableCell />
                                <TableCell>{t('lastname')}</TableCell>
                                <TableCell>{t('firstname')}</TableCell>
                                <TableCell>{t('phone')}</TableCell>
                                <TableCell>{t('email')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {staffs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5}>{t('noStaff')}</TableCell>
                                </TableRow>
                            ) : (
                                staffs.map(({ id, email, firstname, lastname, phone }, index) => (
                                    <TableRow key={index}>
                                        <TableCell
                                            className={styles.CellClick}
                                            onClick={() => navigate(`/staff?profile=${id}`)}
                                        >
                                            <Person />
                                        </TableCell>
                                        <TableCell
                                            className={styles.CellClick}
                                            onClick={() => navigate(`/staff?profile=${id}`)}
                                        >
                                            {lastname ? lastname : <Close className={styles.CloseIcon} />}
                                        </TableCell>
                                        <TableCell
                                            className={styles.CellClick}
                                            onClick={() => navigate(`/staff?profile=${id}`)}
                                        >
                                            {firstname ? firstname : <Close className={styles.CloseIcon} />}
                                        </TableCell>
                                        <TableCell>
                                            {phone ? (
                                                <a href={`tel:${phone}`}>{phone.match(/.{1,2}/g)?.join(' ')}</a>
                                            ) : (
                                                <Close className={styles.CloseIcon} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {email ? (
                                                <a href={`mailto:${email}`}>{email}</a>
                                            ) : (
                                                <Close className={styles.CloseIcon} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <div className={styles.ButtonContainer}>
                    <a
                        href={`/staff?daycare=${showStaffDaycareId}`}
                        className={styles.Button}
                        onClick={(e) => {
                            setCurrentDaycare(showStaffDaycareId)
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/staff?daycare=${showStaffDaycareId}`)
                            }
                        }}
                    >
                        <ButtonTextIcon type={'custom'} customText={t('showStaffList')} customIcon={<Groups />} />
                    </a>
                    <ButtonTextIcon type={'close'} onClick={handleCloseDialog} className={styles.Button} />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default StaffListDialog
